import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ShipmentDetail as ShipmentDetailData } from "api";
import cn from "classnames";
import { CarrierLogo } from "components/Shipments/CarrierLogo";
import { RouteStrip } from "components/Shipments/RouteStrip";
import {
  loadBadge,
  relativeArrivalLabel,
  scheduleLabel,
  shipmentCode,
  statusDetail,
} from "components/Shipments/shipments.model";
import { Modal } from "components/ui/Modal";
import { EMPTY_STATE_CUBE } from "constants/products.constants";
import { useShipmentDetail } from "hooks/useShipmentDetail";
import { useShipmentDocuments } from "hooks/useShipmentDocuments";
import { setLeaveGuard } from "utils/leave.guard";
import { CargoTab } from "./CargoTab";
import { ContainerNoteDrawer } from "./ContainerNoteDrawer";
import { headerMeta, headerNumber, VISIBLE_DETAIL_TABS } from "./detail.model";
import { containerLabel } from "./docs.model";
import { DocumentsDrawer } from "./DocumentsDrawer";
import { DocumentsTab } from "./DocumentsTab";
import { useCargoForm } from "./useCargoForm";

type ShipmentDetailProps = {
  id: number;
  tab: string;
  onSelectTab: (tab: string) => void;
  /** Back to My Shipments — the breadcrumb, and the not-found state's button. */
  onBack: () => void;
  onSessionLost: () => void;
};

/**
 * One shipment's page, in place of the list: the design's `#shipp`. The
 * header reads the saved card; each tab owns its panel.
 */
export const ShipmentDetail = ({ id, tab, onSelectTab, onBack, onSessionLost }: ShipmentDetailProps) => {
  const { shipment, status, retry, replace } = useShipmentDetail(id, onSessionLost);

  return (
    <div className="shipp">
      <nav className="shipp__crumb" aria-label="Breadcrumb">
        <button type="button" className="shipp__crumb-b" onClick={onBack}>
          Shipments
        </button>
        <span aria-hidden="true">/</span>
        <b>{shipment === null ? `QT-${id}` : shipmentCode(shipment)}</b>
      </nav>

      {status === "loading" && <span className="pc-spin shipp__spin" aria-label="Loading" />}

      {status === "failed" && (
        <div className="shipp__state-msg">
          <p className="pc-note pc-note--bad">Could not load this shipment.</p>
          <button type="button" className="pcbtn" onClick={retry}>
            Try again
          </button>
        </div>
      )}

      {status === "unverified" && <p className="pc-note">Verify your email address to see your shipments.</p>}

      {status === "missing" && (
        <div className="pc-empty shipp__missing">
          <img className="pc-empty__cube" src={EMPTY_STATE_CUBE} alt="" width={96} height={96} />
          <div className="pc-empty__h">Shipment not found</div>
          <p className="pc-empty__t">
            There is no shipment {`QT-${id}`} in your company&apos;s list. It may have been removed, or the link points
            to a shipment another account tracks.
          </p>
          <div className="pc-empty__acts">
            <button type="button" className="pcbtn pcbtn--main" onClick={onBack}>
              Back to shipments
            </button>
          </div>
        </div>
      )}

      {status === "ready" && shipment !== null && (
        <LoadedShipment
          shipment={shipment}
          tab={tab}
          onSelectTab={onSelectTab}
          onReplace={replace}
          onSessionLost={onSessionLost}
        />
      )}
    </div>
  );
};

type LoadedShipmentProps = {
  shipment: ShipmentDetailData;
  tab: string;
  onSelectTab: (tab: string) => void;
  /** Repaints the page from a save's response. */
  onReplace: (shipment: ShipmentDetailData) => void;
  onSessionLost: () => void;
};

/** What `#shdoc` holds while open: a saved container's documents, or a row's note. */
type Drawer = { kind: "docs"; containerId: number } | { kind: "note"; containerKey: string } | null;

/** How long the header's Save reads "Saved", as in the design. */
const SAVED_FLASH_MS = 1800;

/**
 * The page once the shipment has loaded.
 *
 * Its documents are fetched once here, for the Documents tab and for the
 * drawer the Cargo tab's container rows open, so the badge is their live
 * count rather than the figure the page opened with. The Cargo tab's edits
 * live here too: the header's Save and Cancel act on them, and while there are
 * any, leaving the page asks first (the design's `#shipp-unsaved-modal`).
 */
const LoadedShipment = ({ shipment, tab, onSelectTab, onReplace, onSessionLost }: LoadedShipmentProps) => {
  const docs = useShipmentDocuments(shipment.id, onSessionLost);
  const cargo = useCargoForm(shipment, onReplace, onSessionLost);
  const [drawer, setDrawer] = useState<Drawer>(null);
  /** The navigation waiting on the unsaved-changes question, or null while nothing is asked. */
  const [leaving, setLeaving] = useState<(() => void) | null>(null);
  const [justSaved, setJustSaved] = useState(false);
  const flash = useRef<number>();

  const documentCount = docs.status === "ready" ? docs.documents.length : shipment.counts.documents;
  const documentCounts = useMemo(
    () =>
      docs.documents.reduce<Record<number, number>>((counts, document) => {
        if (document.container_id !== null) counts[document.container_id] = (counts[document.container_id] ?? 0) + 1;

        return counts;
      }, {}),
    [docs.documents]
  );
  // The header follows the form's load type as it is switched, as the design's does.
  const badge = loadBadge({ ...shipment, load_type: cargo.form.load_type });
  const closeDrawer = useCallback(() => setDrawer(null), []);

  useEffect(() => {
    if (!cargo.dirty) return undefined;

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Older browsers read the prompt off returnValue.
      event.returnValue = "";
    };

    setLeaveGuard((proceed) => setLeaving(() => proceed));
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      setLeaveGuard(null);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [cargo.dirty]);

  useEffect(() => () => window.clearTimeout(flash.current), []);

  const save = async (): Promise<boolean> => {
    const saved = await cargo.save();

    if (saved) {
      setJustSaved(true);
      window.clearTimeout(flash.current);
      flash.current = window.setTimeout(() => setJustSaved(false), SAVED_FLASH_MS);
    }

    return saved;
  };

  const leaveAfter = async (keep: boolean) => {
    const proceed = leaving;

    setLeaving(null);
    if (proceed === null) return;

    if (keep) {
      // A save that fails keeps the visitor here, with its errors on screen.
      if (!(await cargo.save())) return;
    } else {
      cargo.cancel();
    }

    proceed();
  };

  const noteContainerIndex =
    drawer?.kind === "note"
      ? cargo.form.containers.findIndex((container) => container.key === drawer.containerKey)
      : -1;
  const noteContainer = noteContainerIndex === -1 ? null : cargo.form.containers[noteContainerIndex];

  return (
    <>
      <div className="shipp__hd">
        <div className="shipp__logow">
          <CarrierLogo shipment={shipment} className="shipp__logo" size={56} />
          <span className={cn("pcsh__mode", badge.modifier)}>{badge.label}</span>
        </div>
        <div className="shipp__id">
          <div className="shipp__num">{headerNumber(shipment)}</div>
          <p className="shipp__meta">{headerMeta(shipment)}</p>
        </div>
        <div className="shipp__state">
          <span className="shipp__state-i" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="8.6" />
              <path d="M12 7.6V12l3.1 1.9" />
            </svg>
          </span>
          <span className="shipp__state-t">
            <b>{relativeArrivalLabel(shipment)}</b>
            <em>{statusDetail(shipment)}</em>
          </span>
        </div>
        <div className="shipp__acts">
          <button
            type="button"
            className={cn("pcbtn pcbtn--main shipp__save", { "is-ok": justSaved })}
            disabled={cargo.saving}
            onClick={() => void save()}
          >
            <span>{cargo.saving ? "Saving…" : justSaved ? "Saved" : "Save"}</span>
          </button>
          <button type="button" className="pcbtn shipp__cancel" disabled={!cargo.dirty} onClick={cargo.cancel}>
            Cancel
          </button>
        </div>
      </div>

      <RouteStrip shipment={shipment}>
        {scheduleLabel(shipment) !== "—" && <span className="shipp__eta">{scheduleLabel(shipment)}</span>}
      </RouteStrip>

      <div className="shipp__tabs" role="tablist">
        {VISIBLE_DETAIL_TABS.map((detailTab) => (
          <button
            key={detailTab.key}
            type="button"
            className={cn("shipp__tab", { "is-on": detailTab.key === tab })}
            role="tab"
            aria-selected={detailTab.key === tab}
            onClick={() => onSelectTab(detailTab.key)}
          >
            {detailTab.label}
            {detailTab.key === "docs" && <em className="shipp__tab-n">{documentCount}</em>}
          </button>
        ))}
      </div>

      <div className="shipp__body">
        <div className="shipp__tp" role="tabpanel" data-tp={tab}>
          {tab === "cargo" && (
            <CargoTab
              shipment={shipment}
              cargo={cargo}
              documentCounts={documentCounts}
              onOpenDocuments={(containerId) => setDrawer({ kind: "docs", containerId })}
              onOpenNote={(containerKey) => setDrawer({ kind: "note", containerKey })}
            />
          )}
          {tab === "docs" && <DocumentsTab shipment={shipment} docs={docs} />}
        </div>
      </div>

      {drawer?.kind === "docs" && (
        <DocumentsDrawer shipment={shipment} docs={docs} containerId={drawer.containerId} onClose={closeDrawer} />
      )}

      {noteContainer !== null && (
        <ContainerNoteDrawer
          sub={containerLabel(cargo.form, noteContainer, noteContainerIndex)}
          note={noteContainer.note}
          onSave={(note) =>
            cargo.update((current) => ({
              ...current,
              containers: current.containers.map((container) =>
                container.key === noteContainer.key ? { ...container, note } : container
              ),
            }))
          }
          onClose={closeDrawer}
        />
      )}

      {leaving !== null && (
        <Modal
          title="You have unsaved changes"
          subtitle="Changes in this shipment have not been saved yet. Save them before going back to the list?"
          onClose={() => setLeaving(null)}
          actions={
            <>
              <button type="button" className="pcbtn" onClick={() => void leaveAfter(false)}>
                Discard and go back
              </button>
              <button type="button" className="pcbtn pcbtn--main" onClick={() => void leaveAfter(true)}>
                Save and go back
              </button>
            </>
          }
        />
      )}
    </>
  );
};
